import { MenuPopoverProps } from "../../types/Popover";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Menu,
  Stack,
  Typography,
} from "@mui/material";
import styled, { useTheme } from "styled-components";
import {
  ArchivedIcon,
  ArchivedIconMOdal,
  HelpBlackIcon,
  LogoutIcon,
  ProcessingPaymentIcon,
  SaveIcon,
  SettingsIcon,
} from "../../assets/Icons";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import { useNavigate } from "react-router-dom";
import { useContext, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import routeConstant from "../../utils/routeConstant";
import { useSelector } from "react-redux";
import { userDetailsSelector } from "../../store/account/selector";
import { logoutService } from "../../services/AuthServices";
import { AxiosResponse } from "axios";
import { activeSubscriptionSelector } from "../../store/payment/selector";

const ItemRow = ({
  children,
  handleClick,
}: {
  children: React.ReactNode;
  handleClick?: () => void;
}) => {
  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", gap: "0.625rem" }}
      onClick={handleClick}
    >
      {children}
    </Stack>
  );
};

const StyledText = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <Typography
      fontSize="0.938rem"
      fontWeight="500"
      color={theme.colors.black2}
      style={{
        cursor: "pointer",
      }}
    >
      {children}
    </Typography>
  );
};

const NameWithBadge = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0, // important for text ellipsis
});

const ProfilePopover = ({ anchorEl, handlePopoverClose }: MenuPopoverProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const { setAuthStatus } = useContext(AuthContext);

  const handleLogout = async () => {
    setLoading(true);
    const response = (await logoutService()) as AxiosResponse;
    if (response?.status === 200) {
      setAuthStatus(false);
      localStorage.clear();
      setLoading(false);
      navigate(`/${routeConstant.login}`, {
        replace: true,
      });
    } else {
      setLoading(false);
    }
  };

  const userDetails = useSelector(userDetailsSelector);
  const activeSubscription = useSelector(activeSubscriptionSelector);

  const handleSettings = () => {
    navigate(`/${routeConstant.settings}`);
  };

  const handleSavedAds = () => {
    navigate(routeConstant.swipeFiles, {
      replace: true,
    });
  };

  const handleArchiveAds = () => {
    navigate(routeConstant.archiveFiles, {
      replace: true,
    });
  };

  const handlePaymentMethods = () => {
    navigate(routeConstant.paymentMethods, {
      replace: true,
    });
  };

  const getPlanChip = ({
    userDetails,
    activeSubscription,
  }: {
    userDetails: any;
    activeSubscription: any;
  }) => {
    // 🔹 Normalize plan type
    let planType: string | undefined = userDetails?.planType ?? "";

    if (userDetails?.parentPlanDetails?.parentDeatils?._id) {
      planType = "Invited";
    }

    const subType = activeSubscription?.activePlanDetails?.planType;
    if (subType === "Trial") planType = "Trial";
    if (subType === "Free") planType = "Free";
    if (["Monthly", "Yearly"].includes(subType)) planType = "Premium";

    // 🔹 Define chip mapping
    const planChipMap: Record<
      string,
      { label: string; color: "warning" | "success" | "info" | "default" }
    > = {
      Trial: { label: "Trial - Premium", color: "warning" },
      Premium: { label: "Premium", color: "success" },
      Invited: { label: "Invited", color: "info" },
      Free: { label: "Freemium", color: "default" },
    };

    const chipConfig = planChipMap[planType ?? ""];
    if (!chipConfig) return null;

    return (
      <Chip
        label={chipConfig.label}
        color={chipConfig.color}
        size="small"
        sx={{ borderRadius: "8px", fontWeight: 500 }}
      />
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={handlePopoverClose}
      variant="selectedMenu"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      sx={{
        ".MuiMenu-paper": {
          borderRadius: "0.938rem",
          padding: "0.938rem",
          width: "17.375rem",
        },
        ".MuiList-root": {
          display: "flex",
          flexDirection: "column",
          gap: "0.938rem",
          padding: "0.5rem 0",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar
          src={
            userDetails?.profileImage
              ? userDetails?.profileImage
              : "assests/images/user-logo.png"
          }
        />
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <NameWithBadge>
            <Typography
              fontSize="1rem"
              fontWeight="600"
              color={theme.colors.black2}
              noWrap
              sx={{ maxWidth: "180px" }} // adjust as needed
            >
              {userDetails?.fullName}
            </Typography>
            {getPlanChip({ userDetails, activeSubscription })}
          </NameWithBadge>
          <Typography
            fontSize="0.75rem"
            fontWeight="400"
            color={theme.colors.gray1}
            noWrap
          >
            {userDetails?.email}
          </Typography>
        </Stack>
      </Stack>
      <Divider sx={{ marginInline: "-0.938rem" }} />
      <ItemRow handleClick={handleSettings}>
        <SettingsIcon />
        <StyledText>Settings</StyledText>
      </ItemRow>
      <ItemRow handleClick={handleSavedAds}>
        <SaveIcon />
        <StyledText>Saved Ads</StyledText>
      </ItemRow>
      <ItemRow handleClick={handleArchiveAds}>
        <ArchivedIconMOdal />
        <StyledText>Archived Ads</StyledText>
      </ItemRow>
      {userDetails && userDetails?.planId ? (
        <ItemRow handleClick={handlePaymentMethods}>
          <PaymentOutlinedIcon
            fontSize="small"
            color="inherit"
            style={{ color: "#414042" }}
          />
          <StyledText>Payment Method</StyledText>
        </ItemRow>
      ) : null}
      {/* <ItemRow>
        <HelpBlackIcon />
        <StyledText>Help</StyledText>
      </ItemRow> */}
      <Divider sx={{ marginInline: "-0.938rem" }} />
      <ItemRow handleClick={handleLogout}>
        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <LogoutIcon />
        )}
        <StyledText>Logout</StyledText>
      </ItemRow>
    </Menu>
  );
};

export default ProfilePopover;
